import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
 FaSearch, FaMapMarkerAlt, FaDollarSign, FaClock, 
 FaBookmark, FaShare, FaFilter, FaPlus, FaRobot,
FaTimes
} from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';


// Sample job data
const sampleJobs = [
  {
      id: '1',
      title: 'Frontend Developer',
      company: 'WebSolutions',
      location: 'Austin, TX',
      salary: '$90,000 - $140,000',
      type: 'Full-time',
      description: 'Join our team to create responsive and performant web applications...',
      posted: '1 day ago',
      skills: ['React', 'JavaScript', 'HTML/CSS', 'Redux'],
      applicants: 22
    },
  {
    id: '2',
    title: 'UX Designer',
    company: 'DesignHub',
    location: 'New York, NY',
    salary: '$90,000 - $130,000',
    type: 'Full-time',
    description: 'Join our creative team to design intuitive user experiences for our products...',
    posted: '1 week ago',
    skills: ['Figma', 'Adobe XD', 'Sketch', 'User Research'],
    applicants: 32
  },
  {
    id: '3',
    title: 'Data Scientist',
    company: 'DataTech',
    location: 'Remote',
    salary: '$100,000 - $150,000',
    type: 'Full-time',
    description: 'We are looking for a data scientist to work on machine learning projects...',
    posted: '3 days ago',
    skills: ['Python', 'TensorFlow', 'SQL', 'Data Visualization'],
    applicants: 28
  },
  {
    id: '4',
    title: 'Marketing Manager',
    company: 'GrowthCo',
    location: 'Chicago, IL',
    salary: '$80,000 - $120,000',
    type: 'Full-time',
    description: 'Lead our marketing efforts to drive growth and brand awareness...',
    posted: '5 days ago',
    skills: ['Digital Marketing', 'SEO', 'Content Strategy', 'Analytics'],
    applicants: 39
  },

];

const JobCard = ({ job, showAIInsights, userSkills, onApply }) => {
  const [isApplied, setIsApplied] = useState(false);
  const [matchPercentage, setMatchPercentage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const appliedJobs = JSON.parse(localStorage.getItem('appliedJobs') || '[]');
    setIsApplied(appliedJobs.includes(job.id));
  }, [job.id]);

  useEffect(() => {
    if (showAIInsights && matchPercentage === null) {
      setIsLoading(true);
      // Simulating AI processing delay
      const timer = setTimeout(() => {
        const matchedSkills = job.skills.filter(skill => userSkills.includes(skill));
        const percentage = (matchedSkills.length / job.skills.length) * 100;
        setMatchPercentage(Math.round(percentage));
        setIsLoading(false);
      }, 1000 + Math.random() * 1000); // Random delay between 1-2 seconds for each card

      return () => clearTimeout(timer);
    }
  }, [showAIInsights, matchPercentage, job.skills, userSkills]);

  const handleApply = () => {
    if (!isApplied) {
      onApply(job);
      setIsApplied(true);
    }
  };
  return (
    <motion.div 
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-white p-6 rounded-lg shadow-md mb-4 hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-bold text-gray-800">{job.title}</h3>
          <p className="text-gray-600">{job.company}</p>
        </div>
        <div className="flex space-x-2">
          <button className="text-blue-500 hover:text-blue-600 transition-colors duration-300">
            <FaBookmark />
          </button>
          <button className="text-blue-500 hover:text-blue-600 transition-colors duration-300">
            <FaShare />
          </button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
          <FaMapMarkerAlt className="mr-1" /> {job.location}
        </span>
        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center">
          <FaDollarSign className="mr-1" /> {job.salary}
        </span>
        <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center">
          <FaClock className="mr-1" /> {job.type}
        </span>
      </div>
      <p className="mt-4 text-gray-700">{job.description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {job.skills.map((skill, index) => (
          <span key={index} className="bg-gray-200 px-2 py-1 rounded-full text-xs text-gray-700">
            {skill}
          </span>
        ))}
      </div>
      {showAIInsights && (
        <div className="mt-4 border-t pt-4">
          <h4 className="text-lg font-semibold flex items-center">
            <FaRobot className="mr-2 text-purple-500" /> AI Match Insights
          </h4>
          {isLoading ? (
            <div className="flex items-center mt-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500 mr-2"></div>
              <span className="text-gray-600">Analyzing match...</span>
            </div>
          ) : (
            <>
              <div className="flex items-center mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                  <div 
                    className="bg-purple-600 h-2.5 rounded-full" 
                    style={{ width: `${matchPercentage}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-700">{matchPercentage}% Match</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Your skills match {matchPercentage}% of the job requirements. 
                {matchPercentage < 70 && " Consider improving your skills to increase your chances."}
              </p>
            </>
          )}
        </div>
      )}
      <div className="mt-4 flex justify-between items-center">
        <span className="text-sm text-gray-500">Posted {job.posted} • {job.applicants} applicants</span>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`px-4 py-2 rounded ${
            isApplied ? 'bg-green-500' : 'bg-blue-500 hover:bg-blue-600'
          } text-white transition duration-300`}
          onClick={handleApply}
          disabled={isApplied}
        >
          {isApplied ? 'Applied' : 'Apply Now'}
        </motion.button>
      </div>
    </motion.div>
  );
};

const CreateJobForm = ({ onClose, onSubmit }) => {
  const [jobData, setJobData] = useState({
    title: '',
    company: '',
    location: '',
    salary: '',
    type: '',
    description: '',
    skills: []
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJobData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSkillChange = (e) => {
    const skills = e.target.value.split(',').map(skill => skill.trim());
    setJobData(prevData => ({ ...prevData, skills }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(jobData);
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
    >
      <div className="p-6 flex-grow overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Create New Job</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Job Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={jobData.title}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter job title"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="company">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={jobData.company}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
              Location
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={jobData.location}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter job location"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="salary">
              Salary Range
            </label>
            <input
              type="text"
              id="salary"
              name="salary"
              value={jobData.salary}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. $80,000 - $120,000"
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="type">
              Job Type
            </label>
            <select
              id="type"
              name="type"
              value={jobData.type}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Job Type</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Job Description
            </label>
            <textarea
              id="description"
              name="description"
              value={jobData.description}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Enter job description"
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="skills">
              Skills Required (comma-separated)
            </label>
            <input
              type="text"
              id="skills"
              name="skills"
              value={jobData.skills.join(', ')}
              onChange={handleSkillChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. JavaScript, React, Node.js"
            />
          </div>
        </form>
      </div>
      <div className="p-6 bg-gray-100 flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
        >
          Create Job
        </button>
      </div>
    </motion.div>
  );
};

const AIInsightsPanel = ({ jobs, userSkills }) => {
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulating AI processing delay
    setIsLoading(true);
    const timer = setTimeout(() => {
      const generatedInsights = jobs.map(job => {
        const matchedSkills = job.skills.filter(skill => userSkills.includes(skill));
        const matchPercentage = (matchedSkills.length / job.skills.length) * 100;
        return {
          jobId: job.id,
          jobTitle: job.title,
          company: job.company,
          matchPercentage: Math.round(matchPercentage),
          matchedSkills,
          missingSkills: job.skills.filter(skill => !userSkills.includes(skill))
        };
      }).sort((a, b) => b.matchPercentage - a.matchPercentage);

      setInsights(generatedInsights);
      setIsLoading(false);
    }, 2000); // 2 seconds delay

    return () => clearTimeout(timer);
  }, [jobs, userSkills]);

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-lg font-semibold text-gray-700">Analyzing job matches...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-8">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">AI-Powered Job Match Insights</h2>
      <div className="space-y-6">
        {insights.map((insight) => (
          <div key={insight.jobId} className="border-b pb-4 last:border-b-0">
            <h3 className="text-lg font-semibold text-gray-800">{insight.jobTitle} at {insight.company}</h3>
            <div className="flex items-center mt-2">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${insight.matchPercentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-700">{insight.matchPercentage}% Match</span>
            </div>
            <div className="mt-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Matched Skills:</span> {insight.matchedSkills.join(', ') || 'None'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Skills to Improve:</span> {insight.missingSkills.join(', ') || 'None'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const JobBoardPage = () => {
  const { user, canPostJob } = useAuth();
  const [jobs, setJobs] = useState(sampleJobs);
  const [filteredJobs, setFilteredJobs] = useState(jobs);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [createJobOpen, setCreateJobOpen] = useState(false);
  const [filters, setFilters] = useState({
    jobType: [],
    location: [],
    salary: []
  });
  const [selectedJob, setSelectedJob] = useState(null);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [userSkills, setUserSkills] = useState(['JavaScript', 'React', 'Node.js']); // Example user skills


  useEffect(() => {
    setFilteredJobs(jobs);
  }, [jobs]);

  const handleCreateJob = (newJob) => {
    const jobWithId = { ...newJob, id: Date.now().toString(), posted: 'Just now', applicants: 0 };
    setJobs(prevJobs => [jobWithId, ...prevJobs]);
    setCreateJobOpen(false);
  };
  const [jobData, setJobData] = useState({
    title: '',
    company: '',
    location: '',
    salary: '',
    type: '',
    description: '',
    skills: [],
    allowReferral: false // New field
  });

  const handleReferralToggle = () => {
    setJobData(prevData => ({ ...prevData, allowReferral: !prevData.allowReferral }));
  };
  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = jobs.filter(job => 
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredJobs(filtered);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [category]: prevFilters[category].includes(value)
        ? prevFilters[category].filter(item => item !== value)
        : [...prevFilters[category], value]
    }));
  };

  const applyFilters = () => {
    let filtered = [...jobs];
    if (filters.jobType.length > 0) {
      filtered = filtered.filter(job => filters.jobType.includes(job.type));
    }
    if (filters.location.length > 0) {
      filtered = filtered.filter(job => filters.location.some(loc => job.location.includes(loc)));
    }
    if (filters.salary.length > 0) {
      filtered = filtered.filter(job => {
        const jobSalary = parseInt(job.salary.replace(/[^0-9]/g, ''));
        return filters.salary.some(range => {
          const [min, max] = range.split('-').map(num => parseInt(num.replace(/[^0-9]/g, '')));
          return jobSalary >= min && jobSalary <= max;
        });
      });
    }
    setFilteredJobs(filtered);
    setFilterOpen(false);
  };

  const handleApply = (job) => {
    setSelectedJob(job);
  };

  const handleApplicationSubmit = (e) => {
    e.preventDefault();
    const appliedJobs = JSON.parse(localStorage.getItem('appliedJobs') || '[]');
    appliedJobs.push(selectedJob.id);
    localStorage.setItem('appliedJobs', JSON.stringify(appliedJobs));
    setSelectedJob(null);
    setFilteredJobs(prevJobs => prevJobs.map(job => 
      job.id === selectedJob.id ? { ...job, applicants: job.applicants + 1 } : job
    ));
  };

  return (
    <>
      <header className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">Job Board</h1>
          <p className="text-xl max-w-2xl">Access to job postings, mentorship opportunities, and professional networking on mobile devices will support alumni in their career growth and advancement.</p>
        </div>
      </header>
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        <div className="mb-8 flex flex-wrap gap-4">
          <form onSubmit={handleSearch} className="flex-grow">
            <div className="relative">
              <input
                type="text"
                placeholder="Search jobs by title, company, or keywords..."
                className="w-full p-4 pl-12 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </form>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFilterOpen(!filterOpen)}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-blue-600 transition duration-300"
          >
            <FaFilter className="mr-2" /> Filters
          </motion.button>

          {canPostJob && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCreateJobOpen(true)}
              className="bg-green-500 text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-green-600 transition duration-300"
            >
              <FaPlus className="mr-2" /> Post a Job
            </motion.button>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAIInsights(!showAIInsights)}
            className={`${
              showAIInsights ? 'bg-purple-600' : 'bg-purple-500'
            } text-white px-6 py-3 rounded-lg flex items-center shadow-md hover:bg-purple-600 transition duration-300`}
          >
            <FaRobot className="mr-2" /> 
            {showAIInsights ? 'Hide AI Insights' : 'Show AI Insights'}
          </motion.button>
        </div>
        <AnimatePresence>
          {filterOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white p-6 rounded-lg shadow-md mb-8"
            >
              <h2 className="text-xl font-semibold mb-4">Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Job Type</h3>
                  {['Full-time', 'Part-time', 'Contract', 'Internship'].map(type => (
                    <label key={type} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={filters.jobType.includes(type)}
                        onChange={() => handleFilterChange('jobType', type)}
                        className="mr-2"
                      />
                      {type}
                    </label>
                  ))}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Location</h3>
                  {['San Francisco', 'New York', 'Chicago', 'Austin', 'Remote'].map(location => (
                    <label key={location} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={filters.location.includes(location)}
                        onChange={() => handleFilterChange('location', location)}
                        className="mr-2"
                      />
                      {location}
                    </label>
                  ))}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Salary Range</h3>
                  {['0-50k', '50k-100k', '100k-150k', '150k+'].map(range => (
                    <label key={range} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={filters.salary.includes(range)}
                        onChange={() => handleFilterChange('salary', range)}
                        className="mr-2"
                      />
                      {range}
                    </label>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={applyFilters}
                  className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition duration-300"
                >
                  Apply Filters
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div>
          <AnimatePresence>
            {filteredJobs.map(job => (
              <JobCard 
                key={job.id} 
                job={job} 
                showAIInsights={showAIInsights}
                userSkills={userSkills}
                onApply={handleApply}
              />
            ))}
          </AnimatePresence>
        </div>


        <AnimatePresence>
          {selectedJob && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-lg p-6 w-full max-w-md"
              >
                <h2 className="text-2xl font-bold mb-4">Apply for {selectedJob.title}</h2>
                <form onSubmit={handleApplicationSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="resume">
                      Resume (PDF only)
                    </label>
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      accept=".pdf"
                      required
                      className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center justify-between mb-4">
            <span className="text-gray-700 text-sm font-bold">Request Referrals</span>
            <button
              type="button"
              className={`relative inline-flex items-center h-6 rounded-full w-11 focus:outline-none ${
                jobData.allowReferral ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              onClick={handleReferralToggle}
            >
              <span
                className={`inline-block w-4 h-4 transform transition ease-in-out duration-200 rounded-full bg-white ${
                  jobData.allowReferral ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setSelectedJob(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
                    >
                      Submit Application
                    </button>
                  </div>
       
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {createJobOpen && (
            <CreateJobForm
              onClose={() => setCreateJobOpen(false)}
              onSubmit={handleCreateJob}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default JobBoardPage;